import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { Reveal, RevealGroup } from '@/components/atoms';
import { StoryFact } from '@/components/molecules';
import { type CompanyStoryBandModel } from '@/hooks/useCompanyStoryBand';

import { CompanyStoryIntro } from './CompanyStoryIntro';

/**
 * The company story at full length: the band /about opens its argument with.
 *
 * Two columns, with the heading column sticky — the narrative is read on the
 * right while the question it answers stays on screen beside it. The figures
 * are a rail under the narrative rather than a second column of their own,
 * because three years and a city set opposite two paragraphs read as a
 * comparison the band is not making.
 *
 * This layout decides nothing. Every word and figure in it arrives resolved on
 * `model`, from `useCompanyStoryBand`, so no year or address is typed into a
 * component.
 */
export function CompanyStoryFull({ model }: { model: CompanyStoryBandModel }) {
  const { titleId, eyebrow, title, lede, cta, narrative, facts } = model;

  return (
    <Grid container spacing={{ xs: 8, md: 12 }}>
      <Grid size={{ xs: 12, md: 5 }}>
        {/* Outside the `Reveal` on purpose: a sticky element can only travel
            inside its own containing block, and `Reveal` is a block that wraps
            its content, so sticking the heading within it would give the column
            no room to move. Out here the containing block is the grid item,
            which stretches to the tallest column in the row. */}
        <Box sx={{ position: { md: 'sticky' }, top: { md: 120 } }}>
          <Reveal direction="left">
            <CompanyStoryIntro
              titleId={titleId}
              eyebrow={eyebrow}
              title={title}
              lede={lede}
              cta={cta}
            />
          </Reveal>
        </Box>
      </Grid>

      <Grid size={{ xs: 12, md: 7 }}>
        <Stack spacing={{ xs: 8, md: 10 }}>
          <Reveal>
            <Stack spacing={4}>
              {narrative.map((paragraph, index) => (
                <Typography
                  key={paragraph}
                  /* The opening paragraph carries the facts, so it is set a
                     step up; the rest is body copy at a reading measure. */
                  variant={index === 0 ? 'subtitle1' : 'body1'}
                  color={index === 0 ? 'text.primary' : 'text.secondary'}
                  sx={{ maxWidth: '62ch' }}
                >
                  {paragraph}
                </Typography>
              ))}
            </Stack>
          </Reveal>

          <RevealGroup
            component="dl"
            step={80}
            sx={{
              m: 0,
              display: 'grid',
              gap: { xs: 6, md: 5 },
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            }}
          >
            {facts.map((fact) => (
              <StoryFact key={fact.label} fact={fact} />
            ))}
          </RevealGroup>
        </Stack>
      </Grid>
    </Grid>
  );
}
